/**
 * executionType and scheduleType are rows in `value_sets_master`, not hardcoded
 * enums — the integration guide is explicit that the UI must read them from the
 * value set API so an operator adding a type gets a UI for it without a release.
 *
 * The hardcoded lists in `shared.ts` remain as a fallback only: without the
 * seeding migration these calls 404, and a picker with no options would leave
 * the form unusable rather than merely stale.
 */
import { useQuery } from '@tanstack/react-query';
import { valueSetsService } from '../../../services/value-sets.service';
import { EXECUTION_TYPE_OPTIONS, SCHEDULE_TYPE_OPTIONS } from './shared';

export interface Option { label: string; value: string }

function useValueSet(key: string, fallback: Option[]) {
  const q = useQuery({
    queryKey: ['value-set-items', key],
    queryFn: () => valueSetsService.getItems(key),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const options: Option[] = q.data?.length
    ? q.data.map((i) => ({ label: i.label || i.code, value: i.code }))
    : fallback;
  return { options, seeded: !!q.data?.length, loading: q.isLoading };
}

export const useExecutionTypes = () =>
  useValueSet('backgroundJobExecutionType', EXECUTION_TYPE_OPTIONS);

export const useScheduleTypes = () =>
  useValueSet('backgroundJobScheduleType', SCHEDULE_TYPE_OPTIONS);

/**
 * Ships empty on purpose until an operator defines connection profiles, so the
 * caller hides the field rather than showing an empty picker.
 */
export const useConnectionTypes = () => {
  const q = useQuery({
    queryKey: ['value-set-items', 'backgroundJobConnectionType'],
    queryFn: () => valueSetsService.getItems('backgroundJobConnectionType'),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const options: Option[] = (q.data ?? []).map((i) => ({ label: i.label || i.code, value: i.code }));
  return { options, available: options.length > 0 };
};
